/**
 * Gate evaluation for conditionally-visible Summary rows
 * (`SummaryCardRow.visibleWhenFieldContains`).
 *
 * The gating field is a multi-select whose stored shape varies by write path:
 * `string[]` (in-memory), JSON-array string `'["A","B"]'` (UI save), or a
 * comma-space joined string `'A, B'` (AI extraction). Plain single strings also
 * occur. Normalization mirrors `toWorkloadTypeArrayFromForm` and the server's
 * `parseWorkloadSelection`, minus the allowed-label filtering — the gate only asks
 * "does the selection contain this exact value".
 *
 * NOTE: the CSV branch splits on commas, so gate values must be comma-free. The
 * workload labels are; the NVL *config* values would NOT be safe gates.
 *
 * Ported from release/1.10 (shipped 1.10.17). Migration 092 on this branch.
 */

import type { SummaryCardRow } from './summaryCardSourceTypes';

function toSelectionList(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw
      .filter((x): x is string => typeof x === 'string')
      .map(s => s.trim());
  }
  if (typeof raw !== 'string') {
    return [];
  }
  const trimmed = raw.trim();
  if (trimmed === '') {
    return [];
  }
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((x): x is string => typeof x === 'string')
          .map(s => s.trim());
      }
    } catch {
      // Not valid JSON — fall through to CSV / plain handling.
    }
  }
  if (trimmed.includes(',')) {
    return trimmed.split(',').map(s => s.trim());
  }
  return [trimmed];
}

/**
 * True when the row should render: either it has no gate, or the gating field's
 * selection contains the required value. An unanswered gate field (null /
 * undefined / empty) hides the row.
 */
export function isSummaryRowGateSatisfied(
  row: Pick<SummaryCardRow, 'visibleWhenFieldContains'>,
  data: Record<string, unknown>,
): boolean {
  const gate = row.visibleWhenFieldContains;
  if (!gate) {
    return true;
  }
  return toSelectionList(data[gate.dataField]).includes(gate.value);
}
